import { useEffect, useRef, useState } from "react";

export interface PickerItem {
  id: string;
  label: string;
  to?: string;
  active?: boolean;
  accent?: boolean;
  disabled?: boolean;
}

interface PickerButtonProps {
  /** Optional small label rendered above the button (sidebar variant). */
  label?: string;
  value: string;
  items: PickerItem[];
  onPick: (item: PickerItem) => void;
  /** When true, the dropdown anchors to the button width (sidebar). When false,
   *  it sizes to its content (topbar). */
  matchTriggerWidth?: boolean;
  /** Width hint for the trigger button. */
  buttonClassName?: string;
}

export function PickerButton({
  label,
  value,
  items,
  onPick,
  matchTriggerWidth = false,
  buttonClassName = "",
}: PickerButtonProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onClick);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onClick);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div ref={ref} className="relative">
      {label && (
        <div className="text-[10px] font-semibold uppercase tracking-wider text-gray-400 mb-1 px-1">
          {label}
        </div>
      )}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center justify-between gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-50 ${
          matchTriggerWidth ? "w-full" : ""
        } ${buttonClassName}`}
      >
        <span className="truncate text-left">{value}</span>
        <svg width="12" height="12" viewBox="0 0 12 12" className="text-gray-400 shrink-0">
          <path d="M2 4.5l4 4 4-4" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <div
          className={`absolute z-40 mt-1 max-h-72 overflow-y-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg ${
            matchTriggerWidth ? "left-0 right-0" : "left-0 min-w-[14rem]"
          }`}
        >
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                setOpen(false);
                onPick(item);
              }}
              className={`block w-full text-left px-3 py-1.5 text-sm ${
                item.disabled
                  ? "text-gray-400 cursor-default"
                  : item.active
                    ? "bg-gray-50 font-medium text-gray-900"
                    : "text-gray-700 hover:bg-gray-50"
              } ${item.accent && !item.active ? "text-primary font-medium" : ""}`}
            >
              {item.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
